import { faCalendarCheck, faCopy, faCreditCard, faFileCsv, faFilePdf, faHashtag, faRightFromBracket, faRightToBracket } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import { useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { AuthContext } from '../App';
import Navigation from './Navigation';
import Footer from './Footer';

function transactionColor(type) {
    return type === "credit" ? "#66914b" : "#934e4e";
}

function transactionSign(type) {
    return type === "credit" ? "+" : "-";
}

function transactionIcon(type) {
    return type === "credit" ? "/images/pagado_credit.svg" : "/images/pagado_debit.svg";
}

function addressExcerpt(address) {
    const addr = address.split('0a0');
    return addr[0].substring(0, 10) + '{..}' + addr[1].substring(24);
}

const EXPORT_PAGE_SIZE = 500;

function formatDateInputValue(date) {
    const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return offsetDate.toISOString().slice(0, 10);
}

function getDefaultStartDate() {
    const today = new Date();
    return formatDateInputValue(new Date(today.getFullYear(), today.getMonth() - 1, 1));
}

function getDefaultEndDate() {
    return formatDateInputValue(new Date());
}

function formatTransactionDate(value) {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleString('en-GB', { hour12: true });
}

function getTransactionCounterparty(data) {
    const tType = data.transaction_type;
    const address = tType === 'credit' ? data.from_address : data.to_address;
    const user = tType === 'credit' ? data.from_user : data.to_user;
    return user ? user.username : address;
}

function getSignedTransactionAmount(data) {
    return transactionSign(data.transaction_type) + (data.amount ?? '');
}

function getTransactionExportRows(transactions) {
    return transactions.map(data => ([
        formatTransactionDate(data.transaction_date),
        data.transaction_type || '',
        getTransactionCounterparty(data),
        data.from_address || '',
        data.to_address || '',
        getSignedTransactionAmount(data),
        data.balance ?? '',
        data.transaction_hash || '',
    ]));
}

function csvEscape(value) {
    const text = value === null || value === undefined ? '' : String(value);
    return '"' + text.replace(/"/g, '""') + '"';
}

function downloadBlob(content, type, filename) {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

function truncateText(value, maxLength) {
    const text = value === null || value === undefined ? '' : String(value);
    return text.length > maxLength ? text.slice(0, maxLength - 3) + '...' : text;
}

function pdfSafeText(value) {
    return String(value === null || value === undefined ? '' : value)
        .replace(/[^\x20-\x7E]/g, '?')
        .replace(/\\/g, '\\\\')
        .replace(/\(/g, '\\(')
        .replace(/\)/g, '\\)');
}

function pdfTextCommand(text, x, y, size = 8, font = 'F2') {
    return '/' + font + ' ' + size + ' Tf\n1 0 0 1 ' + x + ' ' + y + ' Tm\n(' + pdfSafeText(text) + ') Tj\n';
}

function buildTransactionsPdf(transactions, rangeLabel) {
    const pageWidth = 792;
    const pageHeight = 612;
    const rowsPerPage = 24;
    const rowHeight = 18;
    const left = 36;
    const pages = [];

    for (let i = 0; i < transactions.length; i += rowsPerPage) {
        pages.push(transactions.slice(i, i + rowsPerPage));
    }

    const pageStreams = pages.map((pageRows, pageIndex) => {
        let y = 570;
        let body = 'BT\n';
        body += pdfTextCommand('Pagado Transaction Export', left, y, 16, 'F1');
        y -= 20;
        body += pdfTextCommand('Date range: ' + rangeLabel, left, y, 9, 'F1');
        body += pdfTextCommand('Generated: ' + formatTransactionDate(new Date()), 560, y, 9, 'F1');
        y -= 26;
        body += pdfTextCommand('Date', left, y, 8, 'F1');
        body += pdfTextCommand('Type', 145, y, 8, 'F1');
        body += pdfTextCommand('From / To', 200, y, 8, 'F1');
        body += pdfTextCommand('Amount', 470, y, 8, 'F1');
        body += pdfTextCommand('Balance', 545, y, 8, 'F1');
        body += pdfTextCommand('Hash', 625, y, 8, 'F1');
        y -= 14;

        pageRows.forEach(data => {
            body += pdfTextCommand(formatTransactionDate(data.transaction_date), left, y);
            body += pdfTextCommand(data.transaction_type || '', 145, y);
            body += pdfTextCommand(truncateText(getTransactionCounterparty(data), 38), 200, y);
            body += pdfTextCommand(getSignedTransactionAmount(data), 470, y);
            body += pdfTextCommand(data.balance ?? '', 545, y);
            body += pdfTextCommand(truncateText(data.transaction_hash, 24), 625, y);
            y -= rowHeight;
        });

        body += pdfTextCommand('Page ' + (pageIndex + 1) + ' of ' + pages.length, 690, 28, 8, 'F1');
        body += 'ET\n';
        return body;
    });

    const objects = [];
    const addObject = content => {
        objects.push(content);
        return objects.length;
    };

    const catalogId = addObject('');
    const pagesId = addObject('');
    const fontId = addObject('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');
    const monoFontId = addObject('<< /Type /Font /Subtype /Type1 /BaseFont /Courier >>');
    const pageIds = [];

    pageStreams.forEach(stream => {
        const contentId = addObject('<< /Length ' + stream.length + ' >>\nstream\n' + stream + 'endstream');
        const pageId = addObject('<< /Type /Page /Parent ' + pagesId + ' 0 R /MediaBox [0 0 ' + pageWidth + ' ' + pageHeight + '] /Resources << /Font << /F1 ' + fontId + ' 0 R /F2 ' + monoFontId + ' 0 R >> >> /Contents ' + contentId + ' 0 R >>');
        pageIds.push(pageId);
    });

    objects[catalogId - 1] = '<< /Type /Catalog /Pages ' + pagesId + ' 0 R >>';
    objects[pagesId - 1] = '<< /Type /Pages /Kids [' + pageIds.map(id => id + ' 0 R').join(' ') + '] /Count ' + pageIds.length + ' >>';

    let pdf = '%PDF-1.4\n';
    const offsets = [0];
    objects.forEach((object, index) => {
        offsets[index + 1] = pdf.length;
        pdf += (index + 1) + ' 0 obj\n' + object + '\nendobj\n';
    });

    const xrefOffset = pdf.length;
    pdf += 'xref\n0 ' + (objects.length + 1) + '\n0000000000 65535 f \n';
    for (let i = 1; i <= objects.length; i++) {
        pdf += String(offsets[i]).padStart(10, '0') + ' 00000 n \n';
    }
    pdf += 'trailer\n<< /Size ' + (objects.length + 1) + ' /Root ' + catalogId + ' 0 R >>\nstartxref\n' + xrefOffset + '\n%%EOF';
    return pdf;
}

function Account() {
    const { state } = useContext(AuthContext);
    const [currentRate, setCurrentRate] = useState(null);
    const [stableBalance, setStableBalance] = useState(0);
    const [unstableBalance, setUnstableBalance] = useState(0);
    const [millixAddress, setMillixAddress] = useState('');

    const [trxData, setTrxData] = useState([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [startDate, setStartDate] = useState(getDefaultStartDate);
    const [endDate, setEndDate] = useState(getDefaultEndDate);
    const [exportLoading, setExportLoading] = useState('');
    const [exportError, setExportError] = useState('');
    const [transactionsLoading, setTransactionsLoading] = useState(false);

    const [pendingTrxData, setPendingTrxData] = useState([]);
    const [user, setUser] = useState({});
    const [popupData, setPopupData] = useState({});

    useEffect(() => {
        if (state.isAuthenticated) {
            axios.get(
                `${process.env.REACT_APP_SERVER}/api/millix/address`
            ).then(addrRes => {
                if (addrRes.status !== 200) {
                    setMillixAddress("Invalid address");
                    return;
                }
                setMillixAddress(addrRes.data.address);

                axios.get(
                    `${process.env.REACT_APP_SERVER}/api/millix/balance/${addrRes.data.address}`
                ).then(res => {
                    if (res.status !== 200) {
                        return;
                    }
                    const { stable, unstable } = res.data;

                    setStableBalance(stable);
                    setUnstableBalance(unstable);
                }).catch(err => {
                    console.log(err);
                });
            });

            axios.get(
                `${process.env.REACT_APP_SERVER}/api/millix/current-rate`
            ).then(res => {
                if (res.status !== 200) {
                    return;
                }
                setCurrentRate(res.data);
            }).catch(err => {
                console.log(err);
            });

            fetchPage(0, { startDate, endDate });

            axios.get(
                `${process.env.REACT_APP_SERVER}/api/pagado/pending-transactions`
            ).then(res => {
                if (res.status !== 200) {
                    throw res;
                }
                return res.data;
            }).then(res => {
                setPendingTrxData(res.content);
            }).catch(error => {
                if (error) {
                    console.log(error.data.error);
                }
            });

            if (state.id) {
                axios.post(
                    `${process.env.REACT_APP_SERVER}/api/user/get/${state.id}`
                ).then(
                    res => {
                        if (res.status !== 200) {
                            return;
                        }
                        setUser(res.data);
                    }
                );
            }
        }
    }, [state]);

    const buildTransactionQuery = (pageNum, limit, filters = {}) => {
        const params = new URLSearchParams();
        const activeStartDate = filters.startDate !== undefined ? filters.startDate : startDate;
        const activeEndDate = filters.endDate !== undefined ? filters.endDate : endDate;

        params.set('page', pageNum);
        if (limit) params.set('limit', limit);
        if (activeStartDate) params.set('startDate', activeStartDate);
        if (activeEndDate) params.set('endDate', activeEndDate);

        return params.toString();
    };

    const fetchPage = (pageNum, filters = {}) => {
        const safePage = Math.max(0, pageNum);

        setTransactionsLoading(true);
        axios.get(
            process.env.REACT_APP_SERVER + '/api/pagado/transactions?' + buildTransactionQuery(safePage, null, filters)
        ).then(res => {
            if (res.status !== 200) return;
            const { transactions, total, page, limit } = res.data;
            setTrxData(transactions);
            setPage(page);
            setTotalPages(Math.ceil(total / limit));
            setExportError('');
        }).catch(err => {
            console.log(err);
            setTrxData([]);
            setPage(0);
            setTotalPages(0);
            setExportError(err.response?.data?.error || 'Unable to load transactions for that date range.');
        }).finally(() => {
            setTransactionsLoading(false);
        });
    };

    const getDateRangeLabel = () => {
        if (startDate && endDate) return startDate + ' to ' + endDate;
        if (startDate) return 'From ' + startDate;
        if (endDate) return 'Through ' + endDate;
        return 'All dates';
    };

    const getExportFilename = (extension) => {
        const range = startDate || endDate ? (startDate || 'all') + '_' + (endDate || 'all') : 'all-dates';
        return 'pagado-transactions-' + range + '.' + extension;
    };

    const fetchTransactionsForExport = async () => {
        let exportPage = 0;
        let exportTotal = null;
        const transactions = [];

        do {
            const res = await axios.get(
                process.env.REACT_APP_SERVER + '/api/pagado/transactions?' + buildTransactionQuery(exportPage, EXPORT_PAGE_SIZE)
            );
            const content = res.data.transactions || [];
            exportTotal = res.data.total || 0;
            transactions.push(...content);
            if (content.length === 0) break;
            exportPage += 1;
        } while (transactions.length < exportTotal);

        return transactions;
    };

    const handleExport = async (format) => {
        setExportLoading(format);
        setExportError('');

        try {
            const transactions = await fetchTransactionsForExport();

            if (transactions.length === 0) {
                setExportError('No transactions found for the selected date range.');
                return;
            }

            if (format === 'csv') {
                const headers = ['Date', 'Type', 'From / To', 'From Address', 'To Address', 'Amount', 'Balance', 'Transaction Hash'];
                const rows = getTransactionExportRows(transactions);
                const csv = [headers, ...rows].map(row => row.map(csvEscape).join(',')).join('\n');
                downloadBlob(csv, 'text/csv;charset=utf-8', getExportFilename('csv'));
                return;
            }

            const pdf = buildTransactionsPdf(transactions, getDateRangeLabel());
            downloadBlob(pdf, 'application/pdf', getExportFilename('pdf'));
        } catch (err) {
            console.log(err);
            setExportError(err.response?.data?.error || 'Unable to export transactions. Please try again.');
        } finally {
            setExportLoading('');
        }
    };

    const handleApplyDateRange = () => {
        fetchPage(0, { startDate, endDate });
    };

    const handleClearDateRange = () => {
        const defaultStartDate = getDefaultStartDate();
        const defaultEndDate = getDefaultEndDate();
        const defaultFilters = { startDate: defaultStartDate, endDate: defaultEndDate };

        setStartDate(defaultStartDate);
        setEndDate(defaultEndDate);
        fetchPage(0, defaultFilters);
    };

    const renderPageNumbers = () => {
        const pages = [];
        const visiblePages = 5;

        let start = Math.max(0, page - Math.floor(visiblePages / 2));
        let end = start + visiblePages;

        // Clamp to total pages
        if (end > totalPages) {
            end = totalPages;
            start = Math.max(0, end - visiblePages);
        }

        for (let i = start; i < end; i++) {
            pages.push(
                <button className='btn btn-outline-pagado'
                    key={i}
                    onClick={() => fetchPage(i)}
                    disabled={transactionsLoading || i === page}
                    style={{ fontWeight: i === page ? 'bold' : 'normal' }}
                >
                    {i + 1}
                </button>
            );
        }

        return pages;
    };

    const handleCopy = (e) => {
        navigator.clipboard.writeText(millixAddress);
    };

    const handleSingleTransactionClick = (data, index) => {
        setPopupData(data);

        const popup = document.querySelector('.pagado-popup');
        const popupContent = document.getElementById('single-transaction-details');
        const popupBg = document.querySelector('.popup-bg');

        popup.classList.remove('pagado-hide');

        popupBg.addEventListener('click', (e) => {
            popupContent.parentElement.classList.add('pagado-hide');
        });
    };

    if (state.loading) {
        return <div>Loading...</div>
    }

    return (
        <>
            {!state.isAuthenticated && <Navigate replace to='/login' />}

            <Navigation />
            <div id="appCapsule">
                <div className="section hero full gradientSection">
                    <div className="in">
                        <h5 className="title mb-2">Current Balance</h5>
                        <h2 className="total">
                            Current: {new Intl.NumberFormat().format(stableBalance)} MLX
                            <span>
                                {currentRate && <>{Intl.NumberFormat().format(stableBalance * currentRate)} USD</>}
                            </span>
                        </h2>
                        <h2 className="total">
                            Pending: {new Intl.NumberFormat().format(unstableBalance)} MLX
                            <span>
                                {currentRate && <>{Intl.NumberFormat().format(unstableBalance * currentRate)} USD</>}
                            </span>
                        </h2>
                        {Object.keys(user).length > 0 && <h3 className='text-white'>{`${user.username}`}</h3>}
                        <h4 className="caption">
                            <span className="text-success me-1">
                                Millix address:
                            </span>
                            {millixAddress}
                            <FontAwesomeIcon className='copy-action ms-1' onClick={handleCopy} icon={faCopy} />
                        </h4>
                    </div>
                </div>

                {pendingTrxData.length > 0 ? <>
                    <div className='section mt-4'>
                        <div className='section-heading'>
                            <h2 className='title'>Pending transactions</h2>
                        </div>

                        <div className='pending-transaction-list-wrapper'>
                            <div className='table-responsive pagado-hide-mobile'>
                                <div className='card'>
                                    <table className='transaction-table table table-striped'>
                                        <thead>
                                            <tr>
                                                <th scope='col'>Date</th>
                                                <th scope='col'>From / To</th>
                                                <th className='text-end' scope='col'>Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pendingTrxData.map((data, index) => {
                                                const tType = data.transaction_type;
                                                const address = tType === "credit" ? data.from_address : data.to_address;
                                                const user = tType === "credit" ? data.from_user : data.to_user;

                                                return (
                                                    <tr key={index} className='transaction-single' onClick={() => handleSingleTransactionClick(data, index)}>
                                                        <td>{(new Date(data.create_date)).toLocaleString("en-GB", { hour12: true })}</td>
                                                        <td>{user ? `${user.username}` : address}</td>
                                                        <td className='text-end'>{<span style={{ color: transactionColor(tType) }}>{transactionSign(tType)} {new Intl.NumberFormat().format(data.amount)}</span>}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div className='transaction-list pagado-hide-desktop'>
                            {pendingTrxData.map((data, index) => {
                                const tType = data.transaction_type;
                                const address = tType === "credit" ? data.from_address : data.to_address;
                                const user = tType === "credit" ? data.from_user : data.to_user;

                                return (
                                    <div key={index} className='card' onClick={() => handleSingleTransactionClick(data, index)}>
                                        <div className='transaction-details'>
                                            <div className='date'>{(new Date(data.create_date)).toLocaleDateString(undefined, {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                            })}</div>
                                            <div className='amount'>
                                                <img src={transactionIcon(tType)} alt='' />
                                                {<span style={{ color: transactionColor(tType) }}>
                                                    {new Intl.NumberFormat().format(data.amount)}
                                                </span>}
                                            </div>
                                        </div>
                                        <div className='author-details'>
                                            <div className='img'>
                                                <img src='/images/pagado_icon_xs.png' alt='' />
                                            </div>
                                            <div className='title'>{user ? `${user.username}` : addressExcerpt(address)}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </> : <></>}

                <div className='section mt-4'>
                    <div className='section-heading transaction-heading'>
                        <h2 className='title'>Transactions</h2>
                    </div>
                    <div className='transaction-filter-card'>
                        <div className='transaction-date-range'>
                            <label>
                                <span>Start date</span>
                                <input type='date' value={startDate} max={endDate || undefined} onChange={e => setStartDate(e.target.value)} />
                            </label>
                            <label>
                                <span>End date</span>
                                <input type='date' value={endDate} min={startDate || undefined} onChange={e => setEndDate(e.target.value)} />
                            </label>
                            <button className='btn btn-outline-pagado btn-sm' onClick={handleApplyDateRange} disabled={transactionsLoading}>Apply</button>
                            <button className='btn btn-outline-pagado btn-sm' onClick={handleClearDateRange} disabled={transactionsLoading}>Clear</button>
                            <div className='transaction-export-buttons'>
                                <button className='btn btn-outline-pagado btn-sm' onClick={() => handleExport('csv')} disabled={!!exportLoading || transactionsLoading}>
                                    <FontAwesomeIcon icon={faFileCsv} /> {exportLoading === 'csv' ? 'Exporting...' : 'CSV'}
                                </button>
                                <button className='btn btn-outline-pagado btn-sm' onClick={() => handleExport('pdf')} disabled={!!exportLoading || transactionsLoading}>
                                    <FontAwesomeIcon icon={faFilePdf} /> {exportLoading === 'pdf' ? 'Exporting...' : 'PDF'}
                                </button>
                            </div>
                        </div>
                    </div>
                    {exportError && <p className='transaction-export-error'>{exportError}</p>}
                    <div className='transaction-list-wrapper'>
                        {transactionsLoading ? <div className='transaction-loading' role='status' aria-live='polite'>
                            <img src='/pagado_icon.png' alt='' className='loading-icon' />
                            <span>Loading transactions...</span>
                        </div> : trxData.length > 0 ? <>
                            <div className='table-responsive pagado-hide-mobile'>
                                <div className='card'>
                                    <table className='transaction-table table table-striped'>
                                        <thead>
                                            <tr>
                                                <th scope='col'>Date</th>
                                                <th scope='col'>From / To</th>
                                                <th scope='col'>Amount</th>
                                                <th className='text-end' scope='col'>Balance</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {trxData.map((data, index) => {
                                                const tType = data.transaction_type;
                                                const address = tType === "credit" ? data.from_address : data.to_address;
                                                const user = tType === "credit" ? data.from_user : data.to_user;

                                                return (
                                                    <tr key={index} className='transaction-single' onClick={() => handleSingleTransactionClick(data, index)}>
                                                        <td>{(new Date(data.transaction_date)).toLocaleString("en-GB", { hour12: true })}</td>
                                                        <td>{user ? `${user.username}` : address}</td>
                                                        <td>{<span style={{ color: transactionColor(tType) }}>{transactionSign(tType)} {new Intl.NumberFormat().format(data.amount)}</span>}</td>
                                                        <td className='text-end'>{new Intl.NumberFormat().format(data.balance)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className='transaction-list pagado-hide-desktop'>
                                {trxData.map((data, index) => {
                                    const tType = data.transaction_type;
                                    const address = tType === "credit" ? data.from_address : data.to_address;
                                    const user = tType === "credit" ? data.from_user : data.to_user;

                                    return (
                                        <div key={index} className='card' onClick={() => handleSingleTransactionClick(data, index)}>
                                            <div className='transaction-details'>
                                                <div className='date'>{(new Date(data.transaction_date)).toLocaleDateString(undefined, {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric',
                                                })}</div>
                                                <div className='amount'>
                                                    <img src={transactionIcon(tType)} alt='' />
                                                    {<span style={{ color: transactionColor(tType) }}>
                                                        {new Intl.NumberFormat().format(data.amount)}
                                                    </span>}
                                                </div>
                                            </div>
                                            <div className='author-details'>
                                                <div className='img'>
                                                    <img src='/images/pagado_icon_xs.png' alt='' />
                                                </div>
                                                <div className='title'>{user ? `${user.username}` : addressExcerpt(address)}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </> : <p className='mt-5 text-center'>No transaction data available to display</p>}
                    </div>
                    <div className='pagination d-flex justify-content-center'>
                        <div className='btn-group mt-5' role='group'>
                            <button className='btn btn-outline-pagado' onClick={() => fetchPage(0)} disabled={transactionsLoading || page === 0}>
                                First
                            </button>

                            <button className='btn btn-outline-pagado' onClick={() => fetchPage(page - 1)} disabled={transactionsLoading || page === 0}>
                                Prev
                            </button>

                            {renderPageNumbers()}

                            <button className='btn btn-outline-pagado' onClick={() => fetchPage(page + 1)} disabled={transactionsLoading || page + 1 >= totalPages}>
                                Next
                            </button>

                            <button className='btn btn-outline-pagado' onClick={() => fetchPage(totalPages - 1)} disabled={transactionsLoading || page + 1 >= totalPages}>
                                Last
                            </button>
                        </div>
                    </div>
                </div>

                <div className='pagado-popup pagado-hide'>
                    <div id='single-transaction-details' className='container popup-content'>
                        <div className='container'>
                            <div className='row'>
                                <div className='col'>
                                    <div className='content-table border'>
                                        <div className="entry">
                                            <div className="name">
                                                <FontAwesomeIcon icon={faHashtag} />ID:
                                            </div>
                                            <div className="value">{popupData.transaction_hash}</div>
                                        </div>
                                        <div className="entry">
                                            <div className="name">
                                                <FontAwesomeIcon icon={faCreditCard} />Type:</div>
                                            <div className="value">{popupData.transaction_type ? popupData.transaction_type.toUpperCase() : ''}</div>
                                        </div>
                                        <div className="entry">
                                            <div className="name">
                                                <FontAwesomeIcon icon={faRightFromBracket} />From:</div>
                                            <div className="value">{popupData.from_address}</div>
                                        </div>
                                        <div className="entry">
                                            <div className="name">
                                                <FontAwesomeIcon icon={faRightToBracket} />To:</div>
                                            <div className="value">{popupData.to_address}</div>
                                        </div>
                                        <div className="entry">
                                            <div className="name amount">
                                                <img src="/images/millix_icon-color.png" alt="" />Amount:</div>
                                            <div className="value">{function () {
                                                const sign = transactionSign(popupData.transaction_type);
                                                return (
                                                    <>
                                                        <span style={{ color: transactionColor(popupData.transaction_type) }}>{sign} </span>
                                                        {new Intl.NumberFormat().format(popupData.amount)}
                                                    </>
                                                );
                                            }()}
                                            </div>
                                        </div>
                                        <div className="entry">
                                            <div className="name">
                                                <FontAwesomeIcon icon={faCalendarCheck} />Date:</div>
                                            <div className="value">{(new Date(popupData.transaction_date || popupData.create_date)).toLocaleString("en-GB", { hour12: true })}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='popup-bg'></div>
                </div>
            </div>
            <Footer />
        </>
    );
}

export default Account;
